"use client";

import { ChevronDown, Rss } from "lucide-react";

import { cn } from "@/lib/utils";

import { EmptyState } from "@/components/states/empty-state";
import { QueryBoundary } from "@/components/states/query-boundary";
import { Skeleton } from "@/components/states/skeleton";

import { JobCard } from "@/features/jobs/components/job-card";
import { PlatformStatsView } from "@/features/jobs/components/platform-stats-view";
import {
  LIMIT_OPTIONS,
  ORDER_OPTIONS,
  SORT_OPTIONS,
  useJobsBrowse,
} from "@/features/jobs/hooks/use-jobs-browse";

/*
 * /jobs — the locked "Gradient card" (Railway) design: a plum-tinted dark
 * flat canvas, "All Jobs" / "Platform Stats" tabs, the legacy
 * sort/order/view controls, and a three-up grid of gradient job cards.
 *
 * All browse behaviour lives in `useJobsBrowse`, so tabs, sorting, paging, the
 * counts and the hide control mirror legacy `/jobs` exactly. This file is
 * presentation only.
 */

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * Responsive card grid — always fills the full 1320px content wrapper; NO inner
 * `max-width` cap (an earlier cap made the card row float narrow inside the wide
 * container). `auto-fit` + `minmax(min(100%,20rem),1fr)` fits as many ~20rem
 * columns as the width allows (three across the 1320px canvas) and stretches
 * them flush with `1fr`; with only two jobs the two cards share the row edge to
 * edge instead of sitting centred. `min(100%,20rem)` collapses to a single
 * full-width column on phones so a track never overflows a narrow viewport.
 */
const GRID_CLASS =
  "grid w-full gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,20rem),1fr))]";

function RailSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "h-10 w-full appearance-none rounded-lg border border-input bg-background pl-3 pr-9 text-sm text-foreground transition-colors hover:border-primary/50",
            FOCUS,
          )}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </span>
    </label>
  );
}

/** Page-number list with leading/trailing ellipses around the current page —
 * always anchoring the first and last page (e.g. `1 … 4 5 6 … 20`). */
function pageItems(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: (number | "ellipsis")[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) items.push("ellipsis");
  for (let i = left; i <= right; i += 1) items.push(i);
  if (right < total - 1) items.push("ellipsis");
  items.push(total);
  return items;
}

/** Numbered pager on the dark canvas — mirrors legacy's MUI `Pagination`:
 * `‹ 1 2 3 ›`, rendered whenever there is at least one page. */
function RailPagination({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  const base = cn(
    "grid size-9 place-items-center rounded-lg border border-hairline bg-accent text-sm tabular-nums text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-30",
    FOCUS,
  );
  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-center gap-1.5"
    >
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={base}
      >
        ‹
      </button>
      {pageItems(page, totalPages).map((item, i) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${i}`}
            aria-hidden="true"
            className="grid size-9 place-items-center text-sm text-muted-foreground"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPage(item)}
            aria-label={`Page ${item}`}
            aria-current={item === page ? "page" : undefined}
            className={cn(
              base,
              // Current page: solid --nav-active. border-primary/50 was 1.47:1.
              item === page &&
                "border-nav-active bg-nav-active text-background hover:text-background",
            )}
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
        className={base}
      >
        ›
      </button>
    </nav>
  );
}

/** /jobs — "All Jobs" browse + "Platform Stats" tabs (mirrors the legacy
 * `JobsTabs`). */
export function JobsView() {
  const b = useJobsBrowse();

  // Flat canvas. The layered violet/fuchsia/indigo aurora wash that used to sit
  // behind the page top is deliberately gone (HighBid: "this page is glowing too
  // much") — the page is a plain `bg-background` surface and the cards read
  // against it on their own hairline borders.
  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Fills the viewport rather than sitting in a narrow centred column — the
       * 92% gutter matches /report/job so the tabs, toolbar and card grid all
       * span the same wide content area instead of floating in black space. */}
      <div className="relative mx-auto w-[92%] max-w-[1680px] py-10">
        <div
          role="tablist"
          aria-label="Jobs sections"
          className="mb-8 inline-flex rounded-lg border border-border bg-card p-1"
        >
          {(
            [
              ["all", "All Jobs"],
              ["stats", "Platform Stats"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={b.tab === id}
              onClick={() => b.setTab(id)}
              className={cn(
                "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                FOCUS,
                b.tab === id
                  ? // Solid --nav-active: the fill is what identifies the
                    // selected segment, and bg-primary/15 measured 1.09:1.
                    // text-background (near-black) on it is 6.32:1; white
                    // would be 3.19:1 and fail.
                    "bg-nav-active font-semibold text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {b.tab === "stats" ? (
          <div className="rounded-xl border border-border bg-card p-4">
            <PlatformStatsView />
          </div>
        ) : (
          <>
            {/* Browse toolbar — the result count and the legacy sort/order/view
             * controls share one panel so the row reads as a single unit. */}
            <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <Rss aria-hidden="true" className="size-4 text-primary-text" />
                <span>
                  Showing{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {b.from}–{b.to}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {b.total}
                  </span>{" "}
                  jobs
                </span>
                {b.hiddenCount > 0 ? (
                  <button
                    type="button"
                    onClick={b.showAll}
                    className={cn(
                      "ml-auto rounded-md text-muted-foreground underline underline-offset-4 hover:text-foreground",
                      FOCUS,
                    )}
                  >
                    {b.hiddenCount} hidden · Show all
                  </button>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col gap-3 border-t border-hairline pt-4 sm:flex-row">
                <RailSelect
                  label="Sort by"
                  value={b.params.sortBy ?? "added"}
                  onChange={(v) => b.update({ sortBy: v as never })}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option
                      key={o.value}
                      value={o.value}
                      className="bg-elevated"
                    >
                      {o.label}
                    </option>
                  ))}
                </RailSelect>
                <RailSelect
                  label="Asc/Desc"
                  value={b.params.orientation ?? "desc"}
                  onChange={(v) => b.update({ orientation: v as never })}
                >
                  {ORDER_OPTIONS.map((o) => (
                    <option
                      key={o.value}
                      value={o.value}
                      className="bg-elevated"
                    >
                      {o.label}
                    </option>
                  ))}
                </RailSelect>
                <RailSelect
                  label="View"
                  value={String(b.params.limit)}
                  onChange={(v) =>
                    b.update({ limit: v === "all" ? "all" : Number(v) })
                  }
                >
                  {LIMIT_OPTIONS.map((o) => (
                    <option key={o} value={String(o)} className="bg-elevated">
                      {o === "all" ? "All" : o}
                    </option>
                  ))}
                </RailSelect>
              </div>
            </div>

            <div className="mt-6">
              <QueryBoundary
                query={b.query}
                loadingFallback={
                  <div className={GRID_CLASS}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="h-56 w-full rounded-2xl bg-secondary"
                      />
                    ))}
                  </div>
                }
                isEmpty={() => b.jobs.length === 0}
                emptyFallback={
                  <EmptyState
                    title="No jobs found"
                    description="There are no open jobs right now. Check back soon."
                  />
                }
                errorTitle="We couldn't load jobs"
              >
                {() => (
                  <div className={GRID_CLASS}>
                    {b.jobs.map((job) => (
                      <JobCard
                        key={job.jobId}
                        job={job}
                        onHide={() => b.hide(job.jobId)}
                      />
                    ))}
                  </div>
                )}
              </QueryBoundary>
            </div>

            {/* Rendered whenever there is a page at all, not only when there are
             * two: legacy always renders its pager (MUI count={1} draws
             * "‹ 1 ›"), and hiding it makes a one-page result look different in
             * kind from a two-page one. */}
            {!b.showingAll && b.totalPages > 0 ? (
              <div className="mt-8">
                <RailPagination
                  page={b.page}
                  totalPages={b.totalPages}
                  onPage={(p) => b.setPage(p)}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
